'use client'

import { useState, useEffect } from 'react'
import { FileText, Clock, CheckCircle, XCircle } from 'lucide-react'
import Link from 'next/link'

interface Ticket {
  number: string
  title: string
  description: string
  status: string
  priority: string
  created_at: string
  user: string
}

export default function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTickets()
  }, [])

  const fetchTickets = async () => {
    try {
      const res = await fetch('/api/tickets')
      const data = await res.json()
      setTickets(data.tickets || [])
    } catch (error) {
      console.error('Error fetching tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'new': return <Clock className="w-5 h-5 text-blue-600" />
      case 'open': return <Clock className="w-5 h-5 text-yellow-600" />
      case 'resolved': return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'closed': return <CheckCircle className="w-5 h-5 text-gray-600" />
      default: return <FileText className="w-5 h-5 text-gray-600" />
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tickets de Soporte</h1>
              <p className="text-gray-600 text-sm mt-1">Gestión de tickets creados por el Agente ITSM</p>
            </div>
            <Link 
              href="/" 
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              ← Volver al Demo
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600">Total Tickets</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{tickets.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600">Nuevos</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {tickets.filter(t => t.status === 'New').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600">En Progreso</p>
            <p className="text-3xl font-bold text-yellow-600 mt-2">
              {tickets.filter(t => t.status === 'Open').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600">Resueltos</p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {tickets.filter(t => t.status === 'Resolved').length}
            </p>
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Tickets Recientes</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando tickets...</div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No hay tickets creados aún. <Link href="/" className="text-blue-600 hover:underline">Inicia una conversación</Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tickets.map((ticket, index) => (
                <div key={index} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {getStatusIcon(ticket.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{ticket.title}</h3>
                        <p className="text-gray-600 text-sm mt-1">{ticket.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>#{ticket.number}</span>
                          <span>•</span>
                          <span>{ticket.user}</span>
                          <span>•</span>
                          <span>{new Date(ticket.created_at).toLocaleString('es-MX')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        ticket.priority === 'High' ? 'bg-red-100 text-red-700' :
                        ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        ticket.status === 'New' ? 'bg-blue-100 text-blue-700' :
                        ticket.status === 'Open' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}
